import React, { useEffect, useMemo, useState } from "react";
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from "aws-amplify/auth";

const client = createAuditedClient('HandoverMediaPage');
async function smartAuthMode() {
    const s = await fetchAuthSession();
    if (s?.tokens?.accessToken) return "userPool";
    return "iam";
}
function useQuery() {
    return useMemo(() => new URLSearchParams(window.location.search), []);
}

/**
 * You must add this to schema.graphql and amplify push:
 *
 * type Query {
 *   handoverMedia(handoverId: ID!, token: String!): AWSJSON
 *     @aws_iam
 *     @aws_cognito_user_pools
 *     @function(name: "handoverMedia-${env}")
 * }
 *
 * And your Lambda must return a JSON object shaped like:
 * { handover: {...}, carPhotos:[{key,url}], damagePhotos:[{key,url}], signature:{key,url} }
 */
const HandoverMediaQuery = /* GraphQL */ `
  query HandoverMedia($handoverId: ID!, $token: String!) {
    handoverMedia(handoverId: $handoverId, token: $token)
  }
`;

function safeJsonParse(v) {
    if (v == null) return null;
    if (typeof v === "object") return v;
    if (typeof v !== "string") return null;
    try {
        return JSON.parse(v);
    } catch {
        return null;
    }
}

export default function HandoverMediaPage() {
    const q = useQuery();
    const hid = q.get("hid") || "";
    const token = q.get("t") || "";

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function run() {
            setLoading(true);
            setErr("");
            setData(null);

            if (!hid || !token) {
                setLoading(false);
                setErr("Missing link parameters. Please use the original email link.");
                return;
            }

            try {
                const res = await client.graphql({
                    query: HandoverMediaQuery,
                    variables: { handoverId: hid, token },
                    authMode: await smartAuthMode(),
                });

                const raw = res?.data?.handoverMedia;
                const json = safeJsonParse(raw) || raw;

                if (!json) throw new Error("No data returned.");
                if (!cancelled) setData(json);
            } catch (e) {
                if (!cancelled) setErr(e?.message || "Failed to load handover media.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        run();
        return () => {
            cancelled = true;
        };
    }, [hid, token]);

    const title =
        data?.handover?.type === "SUBSTITUTION"
            ? "Vehicle Substitution Photos"
            : data?.handover?.type === "DROPOFF"
                ? "Vehicle Return Photos"
                : "Vehicle Pick-up Photos";

    const carPhotos = Array.isArray(data?.carPhotos) ? data.carPhotos : [];
    const damagePhotos = Array.isArray(data?.damagePhotos) ? data.damagePhotos : [];
    const signature = data?.signature || null;

    return (
        <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 18 }}>
            <div
                style={{
                    maxWidth: 980,
                    margin: "0 auto",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    overflow: "hidden",
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
                }}
            >
                <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                        {title}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                        Handover ID: <span style={{ color: "#111827" }}>{hid}</span>
                    </div>
                </div>

                <div style={{ padding: 16 }}>
                    {loading && <div style={{ color: "#111827" }}>Loading…</div>}

                    {!loading && err && (
                        <div
                            style={{
                                border: "1px solid #fecaca",
                                background: "#fef2f2",
                                color: "#991b1b",
                                padding: 12,
                                borderRadius: 12,
                                fontWeight: 600,
                            }}
                        >
                            {err}
                        </div>
                    )}

                    {!loading && !err && data && (
                        <>
                            <Section title="Car photos" items={carPhotos} />
                            <Section title="Damage photos" items={damagePhotos} />
                            <SignatureSection signature={signature} />

                            <div style={{ marginTop: 14, fontSize: 12, color: "#6b7280" }}>
                                This page shows time-limited links to handover media.
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Section({ title, items }) {
    if (!items.length) {
        return (
            <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>{title}</div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>No items.</div>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 800, marginBottom: 10, color: "#111827" }}>
                {title} ({items.length})
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 10,
                }}
            >
                {items.map((it, idx) => {
                    const url = it?.url || it?.signedUrl || it;
                    const key = it?.key || it?.s3Key || `item-${idx}`;
                    return (
                        <a
                            key={key}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 12,
                                overflow: "hidden",
                                display: "block",
                                textDecoration: "none",
                                background: "#fff",
                            }}
                            title="Open full size"
                        >
                            <img
                                src={url}
                                alt={key}
                                style={{
                                    width: "100%",
                                    height: 180,
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                            <div style={{ padding: 8, fontSize: 11, color: "#6b7280" }}>
                                {key}
                            </div>
                        </a>
                    );
                })}
            </div>
        </div>
    );
}

function SignatureSection({ signature }) {
    if (!signature) {
        return (
            <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Signature</div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>No signature.</div>
            </div>
        );
    }

    const url = signature?.url || signature?.signedUrl || signature;
    const key = signature?.key || signature?.s3Key || "signature";

    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 800, marginBottom: 10, color: "#111827" }}>
                Signature
            </div>
            <div
                style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    overflow: "hidden",
                    maxWidth: 520,
                    background: "#fff",
                }}
            >
                <img
                    src={url}
                    alt={key}
                    style={{
                        width: "100%",
                        height: 220,
                        objectFit: "contain",
                        display: "block",
                        background: "#f9fafb",
                    }}
                />
                <div style={{ padding: 8, fontSize: 11, color: "#6b7280" }}>{key}</div>
            </div>
        </div>
    );
}
